/**
 * Session conditions: the time of day and weather the player picks. One data row per preset;
 * the tower view reads the visual fields and the sim reads the wind.
 *
 * Assumptions: presets keep the airport's prevailing wind direction and only scale its speed,
 * since the preferred runways in the airport file were chosen for that direction. "Now" is mean
 * solar time at the airport longitude (no time zones or DST), which is also what the tower
 * view's sun model expects. Weather changes nothing operationally (no approach minima).
 */
import type { EnvState } from './types';

export type TimeOfDay = 'now' | 'dawn' | 'day' | 'dusk' | 'night';
export type Weather = 'clear' | 'scattered' | 'overcast' | 'rain' | 'fog' | 'storm';

export const TIMES_OF_DAY: readonly TimeOfDay[] = ['now', 'dawn', 'day', 'dusk', 'night'];
export const WEATHERS: readonly Weather[] = ['clear', 'scattered', 'overcast', 'rain', 'fog', 'storm'];

export const TIME_OF_DAY_LABEL: Record<TimeOfDay, string> = { now: 'Now', dawn: 'Dawn', day: 'Day', dusk: 'Dusk', night: 'Night' };
/** Hours after sunrise that "dawn" starts, and before sunset that "dusk" starts: the sun is a few degrees up, the light is low and warm. */
const TWILIGHT_OFFSET_H = 0.5;

export interface WeatherPreset {
  label: string;
  visibilityM: number;
  cloudCeilingFt: number;
  /** 0 clear to 1 overcast. */
  cloudCoverage: number;
  rain: boolean;
  /** Multiplies the airport's prevailing wind speed. */
  windScale: number;
  /** Gusting, shifting wind even where the realism level would hold it steady. */
  gusty: boolean;
}

export const WEATHER: Record<Weather, WeatherPreset> = {
  clear: { label: 'Clear', visibilityM: 50000, cloudCeilingFt: 8000, cloudCoverage: 0, rain: false, windScale: 0.8, gusty: false },
  scattered: { label: 'Scattered', visibilityM: 40000, cloudCeilingFt: 4500, cloudCoverage: 0.3, rain: false, windScale: 1, gusty: false },
  overcast: { label: 'Overcast', visibilityM: 15000, cloudCeilingFt: 2500, cloudCoverage: 0.9, rain: false, windScale: 1, gusty: false },
  rain: { label: 'Rain', visibilityM: 6000, cloudCeilingFt: 1500, cloudCoverage: 0.95, rain: true, windScale: 1.3, gusty: false },
  fog: { label: 'Fog', visibilityM: 600, cloudCeilingFt: 200, cloudCoverage: 1, rain: false, windScale: 0.3, gusty: false },
  storm: { label: 'Storm', visibilityM: 3000, cloudCeilingFt: 1000, cloudCoverage: 1, rain: true, windScale: 2, gusty: true },
};

export interface ConditionsAirport { icao: string; lat: number; lon: number; wind: { directionDeg: number; speedKt: number } }

/** Wind a preset gives this airport. */
export function conditionsWind(airport: ConditionsAirport, weather: Weather): { directionDeg: number; speedKt: number; gusty: boolean } {
  const w = WEATHER[weather];
  return { directionDeg: airport.wind.directionDeg, speedKt: Math.round(airport.wind.speedKt * w.windScale), gusty: w.gusty };
}

/** Local mean solar hour at a longitude for a moment, 0-24. */
export function solarHours(lonDeg: number, at: Date): number {
  const utc = at.getUTCHours() + at.getUTCMinutes() / 60 + at.getUTCSeconds() / 3600;
  return (((utc + lonDeg / 15) % 24) + 24) % 24;
}

export function dayOfYear(d: Date): number {
  return Math.floor((d.getTime() - Date.UTC(d.getUTCFullYear(), 0, 0)) / 86400000);
}

/**
 * Local solar sunrise and sunset hours: the geometric sun (no refraction), with the same
 * declination formula the tower view lights the scene with. Polar day or night clamps.
 */
export function sunriseSunset(latDeg: number, doy: number): { sunrise: number; sunset: number } {
  const rad = Math.PI / 180;
  const decl = 23.44 * rad * Math.sin((2 * Math.PI * (284 + doy)) / 365);
  const cosH = Math.max(-1, Math.min(1, -Math.tan(latDeg * rad) * Math.tan(decl)));
  const half = Math.acos(cosH) / rad / 15;
  return { sunrise: 12 - half, sunset: 12 + half };
}

/** Hour the session's clock starts at; "now" is read once, when the session opens. */
export function startHours(airport: ConditionsAirport, timeOfDay: TimeOfDay, at: Date = new Date()): number {
  if (timeOfDay === 'now') return solarHours(airport.lon, at);
  if (timeOfDay === 'day') return 12;
  if (timeOfDay === 'night') return 23;
  const sun = sunriseSunset(airport.lat, dayOfYear(at));
  return timeOfDay === 'dawn' ? sun.sunrise + TWILIGHT_OFFSET_H : sun.sunset - TWILIGHT_OFFSET_H;
}

/** Everything the tower view draws, with the clock advanced by the sim time elapsed. */
export function resolveConditions(airport: ConditionsAirport, weather: Weather, startHour: number, elapsedS = 0): Required<Omit<EnvState, 'dayOfYear'>> {
  const w = WEATHER[weather];
  const wind = conditionsWind(airport, weather);
  return {
    timeOfDayHours: (((startHour + elapsedS / 3600) % 24) + 24) % 24,
    visibilityM: w.visibilityM,
    cloudCeilingFt: w.cloudCeilingFt,
    cloudCoverage: w.cloudCoverage,
    rain: w.rain,
    windDirDeg: wind.directionDeg,
    windKt: wind.speedKt,
  };
}

/** A METAR-shaped summary line, e.g. "KSFO 28012KT 9999 SCT045". Not a real report. */
export function metarLine(icao: string, env: { windDirDeg: number; windKt: number; visibilityM: number; cloudCeilingFt: number; cloudCoverage: number; rain: boolean }, gusty = false): string {
  const kt = Math.round(env.windKt);
  const wind = kt < 1 ? '00000KT' : `${String(Math.round(env.windDirDeg) % 360 || 360).padStart(3, '0')}${String(kt).padStart(2, '0')}${gusty ? `G${String(Math.round(kt * 1.5)).padStart(2, '0')}` : ''}KT`;
  const vis = env.visibilityM >= 10000 ? '9999' : String(Math.round(env.visibilityM / 100) * 100).padStart(4, '0');
  const wx = env.rain ? (gusty ? ' TSRA' : ' RA') : env.visibilityM < 1000 ? ' FG' : '';
  const base = String(Math.round(env.cloudCeilingFt / 100)).padStart(3, '0');
  const c = env.cloudCoverage;
  const cloud = c < 0.05 ? 'SKC' : c < 0.45 ? `SCT${base}` : c < 0.9 ? `BKN${base}` : c < 1 ? `OVC${base}` : env.visibilityM < 1000 ? `VV${base}` : `OVC${base}`;
  return `${icao.toUpperCase()} ${wind} ${vis}${wx} ${cloud}`;
}

/** "19:15" from an hour of the day. */
export function clockText(hours: number): string {
  const m = Math.round((((hours % 24) + 24) % 24) * 60) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
